import { createApp } from "vue";
import { createPinia } from "pinia";

// tooltip directives
import { VTooltip, VClosePopper } from "floating-vue";

// toasts
import Toast from "vue-toastification";
import type { PluginOptions as VueToastificationPluginOptions } from "vue-toastification";

// i18n - translations
import { createI18n } from "vue-i18n";
import messages from "@/assets/translations/messages.pl.json";

// g-components
import gComponentsTranslations from "@gwrk/g-components/src/translations/translations.pl.json";

// global styles
import "@/assets/scss/global.scss";

// sentry
import * as Sentry from "@sentry/vue";

// app
import App from "./App.vue";
import { routerInstance } from "./config/router";

// tools
import { merge } from "lodash-es";

const i18n = createI18n({
  locale: "pl",
  messages: {
    pl: merge(messages, gComponentsTranslations),
  },
});

const vueToastificationPluginOptions: VueToastificationPluginOptions = {
  hideProgressBar: true,
  timeout: 3000,
};

const app = createApp(App);

// Sentry envs are defined only during production build, meaning that Sentry is only initialized in production
Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  release: import.meta.env.VITE_SENTRY_RELEASE_NAME,
  integrations: [
    Sentry.browserTracingIntegration({ router: routerInstance }),
    Sentry.replayIntegration(),
  ],
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for tracing.
  // We recommend adjusting this value in production
  tracesSampleRate: 0.02,
  tracePropagationTargets: ["https://gowork.pl", /^\//],
});

app
  .use(createPinia())
  .use(routerInstance)
  .use(i18n)
  .directive("tooltip", VTooltip)
  .directive("close-popper", VClosePopper)
  .use(Toast, vueToastificationPluginOptions)
  .mount("#app");
